import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { TopupTemplateService } from './topupTemplateService';
import secureApiService from './secureApiService';

export class TopupOrderService {
  static collectionName = 'topupOrders';

  static STATUSES = {
    PENDING: 'pending',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    REFUSED: 'refused'
  };

  static NOTIFICATION_CHANNELS = {
    IN_APP: 'in_app',
    EMAIL: 'email',
    TELEGRAM: 'telegram'
  };

  static normalizeDate(value) {
    if (value?.toDate) {
      return value.toDate();
    }

    return value || null;
  }

  static normalizeOrderDocument(docSnapshot) {
    const data = docSnapshot.data() || {};

    return {
      id: docSnapshot.id,
      ...data,
      createdAt: this.normalizeDate(data.createdAt),
      updatedAt: this.normalizeDate(data.updatedAt),
      processedAt: this.normalizeDate(data.processedAt),
      assignedAt: this.normalizeDate(data.assignedAt)
    };
  }

  static resolveResellerName(userProfile = {}) {
    const fullName = `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim();
    return (
      String(userProfile.displayName || '').trim() ||
      fullName ||
      String(userProfile.email || '').trim() ||
      'Revendeur'
    );
  }

  static sanitizeFormData(formValues = {}, templateFields = []) {
    return templateFields.reduce((result, field) => {
      if (field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION) {
        return result;
      }

      result[field.id] = String(formValues[field.id] || '').trim();
      return result;
    }, {});
  }

  static sanitizeFormFields(templateFields = []) {
    return templateFields
      .filter((field) => field.type !== TopupTemplateService.FIELD_TYPES.INSTRUCTION)
      .map((field) => ({
        id: field.id,
        label: field.label || field.id,
        type: field.type || TopupTemplateService.FIELD_TYPES.TEXT,
        required: Boolean(field.required)
      }));
  }

  static buildNotificationChannels(channels = []) {
    const allowedChannels = Object.values(this.NOTIFICATION_CHANNELS);
    const normalizedChannels = Array.isArray(channels)
      ? channels.filter((channel) => allowedChannels.includes(channel))
      : [];

    return normalizedChannels.length > 0
      ? Array.from(new Set(normalizedChannels))
      : [this.NOTIFICATION_CHANNELS.IN_APP];
  }

  static buildCreatePayload({
    userId,
    userProfile,
    product,
    template,
    offer,
    formValues,
    templateFields,
    notificationChannels
  }) {
    if (!userId || !product || !offer) {
      throw new Error('Donnees Topup invalides');
    }

    const productName = String(product.game || product.name || 'Topup').trim();
    const offerName = String(offer.name || offer.value || '').trim();
    const offerValue = String(offer.value || offer.name || '').trim();
    const submissionMode = template?.submissionMode || product.topupFulfillmentMode ||
      TopupTemplateService.SUBMISSION_MODES.DASHBOARD;
    const price = Number(offer.price || 0);

    if (!price || Number.isNaN(price) || price <= 0) {
      throw new Error('Prix Topup invalide');
    }

    return {
      userId,
      resellerName: this.resolveResellerName(userProfile),
      resellerEmail: String(userProfile?.email || '').trim(),
      productId: product.id,
      productName,
      productImageUrl: String(product.detailImageUrl || product.imageUrl || product.coverImageUrl || '').trim(),
      categoryId: String(product.categoryId || '').trim(),
      categoryName: String(product.category || 'Topup').trim(),
      section: 'topup',
      templateId: String(template?.id || product.topupTemplateId || '').trim(),
      templateName: String(template?.name || product.topupTemplateName || '').trim(),
      templateFamily: String(template?.family || product.topupTemplateFamily || '').trim(),
      submissionMode,
      whatsappNumber: String(template?.whatsappNumber || '').trim(),
      offerId: String(offer.id || '').trim(),
      offerName,
      offerValue,
      price,
      formData: this.sanitizeFormData(formValues, templateFields),
      formFields: this.sanitizeFormFields(templateFields),
      notificationChannels: this.buildNotificationChannels(notificationChannels),
      status: this.STATUSES.PENDING,
      adminNote: ''
    };
  }

  static async createOrder(orderData) {
    try {
      const payload = this.buildCreatePayload(orderData);
      const result = await secureApiService.submitTopupOrder(payload);

      return result;
    } catch (error) {
      console.error('Erreur lors de la creation de la commande Topup:', error);
      throw error;
    }
  }

  static async getOrderById(orderId) {
    try {
      const orderRef = doc(db, this.collectionName, orderId);
      const orderDoc = await getDoc(orderRef);

      if (!orderDoc.exists()) {
        return null;
      }

      return this.normalizeOrderDocument(orderDoc);
    } catch (error) {
      console.error('Erreur lors du chargement de la commande Topup:', error);
      throw error;
    }
  }

  static async getUserOrders(userId) {
    try {
      const ordersQuery = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(ordersQuery);
      return snapshot.docs.map((docSnapshot) => this.normalizeOrderDocument(docSnapshot));
    } catch (error) {
      console.error('Erreur lors du chargement des commandes Topup utilisateur:', error);
      throw error;
    }
  }

  static async getAllOrders() {
    try {
      const ordersQuery = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(ordersQuery);
      return snapshot.docs.map((docSnapshot) => this.normalizeOrderDocument(docSnapshot));
    } catch (error) {
      console.error('Erreur lors du chargement des commandes Topup admin:', error);
      throw error;
    }
  }

  static async updateOrderStatus(orderId, { status, adminNote = '', proofImageUrl = '' }) {
    try {
      const allowedStatuses = Object.values(this.STATUSES);
      if (!allowedStatuses.includes(status)) {
        throw new Error('Statut Topup invalide');
      }

      return await secureApiService.processTopupOrder(
        orderId,
        status,
        String(adminNote || '').trim(),
        String(proofImageUrl || '').trim()
      );
    } catch (error) {
      console.error('Erreur lors de la mise a jour de la commande Topup:', error);
      throw error;
    }
  }

  static async claimOrder(orderId) {
    try {
      return await secureApiService.claimTopupOrder(orderId);
    } catch (error) {
      console.error('Erreur lors de la prise en charge de la commande Topup:', error);
      throw error;
    }
  }

  static async releaseOrder(orderId) {
    try {
      return await secureApiService.releaseTopupOrder(orderId);
    } catch (error) {
      console.error('Erreur lors du relachement de la commande Topup:', error);
      throw error;
    }
  }

  static getStatusLabel(status) {
    if (status === this.STATUSES.IN_PROGRESS) return 'En cours';
    if (status === this.STATUSES.COMPLETED) return 'Terminee';
    if (status === this.STATUSES.REFUSED) return 'Refusee';
    return 'En attente';
  }
}

export default TopupOrderService;
